import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api, apiBlob, saveBlob } from '../../api/http';
import { Notice, errorMessage, secondaryButton } from '../../components/common';
import { t } from '../../i18n';

interface PublishedResult {
  examId: string;
  examName: string;
  startDate: string;
  percentage: number;
  grade: string;
  passed: boolean;
  classRank: number;
}

/** Published exam results of one child, with the report card PDF. */
export function ChildResults({ studentId }: { studentId: string }) {
  const [message, setMessage] = useState<string | null>(null);
  const results = useQuery({
    queryKey: ['me', 'children', studentId, 'results'],
    queryFn: () => api<PublishedResult[]>(`/me/children/${studentId}/results`),
  });

  const download = async (r: PublishedResult) => {
    try {
      saveBlob(
        await apiBlob(`/me/children/${studentId}/results/${r.examId}/report-card.pdf`),
        `report-card-${r.examName}.pdf`,
      );
    } catch (e) {
      setMessage(errorMessage(e));
    }
  };

  if (results.isPending) return <p role="status">{t('common.loading')}</p>;
  if (results.isError) return <Notice kind="error">{errorMessage(results.error)}</Notice>;
  return (
    <section aria-label={t('child.tab.results')} className="space-y-3">
      {message && <Notice kind="error">{message}</Notice>}
      {results.data.length === 0 && <p className="text-slate-600">{t('child.results.none')}</p>}
      <ul className="space-y-2">
        {results.data.map((r) => (
          <li
            key={r.examId}
            className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white p-3 text-sm"
          >
            <span>
              <span className="font-medium">{r.examName}</span>{' '}
              <span className="text-slate-600">
                {r.percentage}% · {t('results.grade')} {r.grade} ·{' '}
                {r.passed ? t('results.pass') : t('results.fail')} ·{' '}
                {t('child.results.rank', { rank: r.classRank })}
              </span>
            </span>
            <button
              type="button"
              className={secondaryButton}
              aria-label={t('results.pdfFor', { name: r.examName })}
              onClick={() => void download(r)}
            >
              {t('results.pdf')}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
